/**
 * Application_Form — pure Bound_Field logic
 * (application-form-builder spec, task 4.1).
 *
 * A Bound_Field renders a control but reads and writes a *canonical entity* (its
 * Binding_Target) and stores **no** duplicate value in the Application_Response.
 * This module is the single source of the rules that govern that behavior:
 *
 *   - which canonical writer a bound write routes to (`writerForTarget`,
 *     Req 17.4);
 *   - the no-stored-bound-value invariant on the response `values` map
 *     (`boundFieldIds` / `assertNoBoundValues`, Req 17.2/17.6);
 *   - resolving a bound field's display value *only* from its canonical source
 *     (`resolveBoundField`, Req 9.1/10.6, 18.3);
 *   - the Division selector's options and the validity of a Division selection
 *     against the Division_Registry (`divisionSelectorOptions` /
 *     `validateDivisionSelection`, Req 18.1/18.4);
 *   - that Contact/identity bound fields are locked read-only in the application
 *     (`isContactIdentityLocked`, Req 17.3).
 *
 * It contains NO AWS SDK calls and NO I/O. The thin adapter performs the actual
 * writes (against PersonService / Participation / DocumentReference) and the
 * actual reads (loading the `CanonicalSources`); the *rules* about what a bound
 * field stores, resolves to, and is allowed to write live here — exactly the
 * "pure core, thin adapter" precedent of `division-registry/registryLogic.ts`
 * and `person-service/personLogic.ts`.
 *
 * Validates: Requirements 17.1, 17.2, 17.3, 17.4, 17.5, 17.6, 18.1, 18.3, 18.4
 */

import type { Division } from '../division-registry/registryLogic';
import { isApplicationSelectable } from '../division-registry/registryLogic';
import type { PersonAddress } from './contactIntake';
import type {
  BindingTarget,
  FieldValidationError,
  FormFieldDef,
  FormVersionSnapshot,
  ResponseValue,
} from './fieldValidation';

// Re-export the base category/target types so callers can import the
// bound-field vocabulary from this module (the conceptual home of the rules)
// while the types themselves remain defined once in the lowest module.
export type { BindingTarget, FieldCategory } from './fieldValidation';

// ---------------------------------------------------------------------------
// Binding_Target enumeration
// ---------------------------------------------------------------------------

/**
 * The full `BindingTarget` union enumerated as a runtime array — the single
 * source for iterating every canonical entity a Bound_Field can reference
 * (including the additive `SEASON` target, director-form-application-cutover
 * Req 4.1/4.2). Kept in sync with the `BindingTarget` type in `fieldValidation`;
 * `writerForTarget` is total over exactly these values.
 */
export const BINDING_TARGETS: readonly BindingTarget[] = [
  'DIVISION',
  'TITLE_PREFERENCES',
  'CONTACT_IDENTITY',
  'DOCUMENT_SLOT',
  'SEASON',
] as const;

// ---------------------------------------------------------------------------
// Canonical writer routing (Req 17.4)
// ---------------------------------------------------------------------------

/**
 * The canonical writer a bound write must be routed to. A pure descriptor — the
 * adapter maps each value onto the concrete service/store that performs the I/O
 * (PersonService, the Participation service, or the DocumentReference store).
 */
export type CanonicalWriter = 'PERSON_SERVICE' | 'PARTICIPATION' | 'DOCUMENT_REFERENCE';

/**
 * Route a Bound_Field write to the canonical writer for its Binding_Target
 * (Req 17.4):
 *
 *   - Contact/identity → PersonService (the sole writer of Person identity);
 *   - Division, Title, and Season → Participation (`divisionKey` /
 *     `approvedTitle` / `competitionYear`);
 *   - Document slot → DocumentReference (the unchanged document store).
 *
 * Total over the `BindingTarget` union.
 */
export function writerForTarget(target: BindingTarget): CanonicalWriter {
  switch (target) {
    case 'CONTACT_IDENTITY':
      return 'PERSON_SERVICE';
    case 'DIVISION':
    case 'TITLE_PREFERENCES':
    // Season selection writes Participation.competitionYear — the same
    // canonical writer as DIVISION (director-form-application-cutover, Req 4.2).
    case 'SEASON':
      return 'PARTICIPATION';
    case 'DOCUMENT_SLOT':
      return 'DOCUMENT_REFERENCE';
  }
}

// ---------------------------------------------------------------------------
// No-stored-bound-value invariant (Req 17.2, 17.6)
// ---------------------------------------------------------------------------

/**
 * The set of Bound_Field ids in a captured version snapshot. A Bound_Field
 * stores no value in the Application_Response, so its id must never appear in
 * the response `values` map.
 */
export function boundFieldIds(snapshot: FormVersionSnapshot): Set<string> {
  const ids = new Set<string>();
  for (const page of snapshot.pages) {
    for (const field of page.fields) {
      if (field.fieldCategory === 'BOUND') {
        ids.add(field.id);
      }
    }
  }
  return ids;
}

/**
 * Assert that no Bound_Field id carries a stored value in the response `values`
 * map (Req 17.2, 17.6). Returns a `BOUND_VALUE_NOT_ALLOWED` error for each
 * offending id, or an empty list when the invariant holds. Pure and total; the
 * inputs are never mutated.
 *
 * This is the standalone, snapshot-scoped form of the same check
 * `fieldValidation.validateResponse` performs inline, exposed here so the
 * adapter can enforce canonical integrity at the bound-field boundary
 * independently of full response validation.
 */
export function assertNoBoundValues(
  snapshot: FormVersionSnapshot,
  values: Record<string, ResponseValue>,
): FieldValidationError[] {
  const bound = boundFieldIds(snapshot);
  const errors: FieldValidationError[] = [];
  for (const fieldId of Object.keys(values)) {
    if (bound.has(fieldId)) {
      errors.push({
        fieldId,
        code: 'BOUND_VALUE_NOT_ALLOWED',
        message: `Bound field "${fieldId}" must not store a value in the response; it reads and writes its canonical entity.`,
      });
    }
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Canonical read-model + resolution (Req 9.1, 10.6, 18.3)
// ---------------------------------------------------------------------------

/**
 * A reference to a document held in the DocumentReference store, optionally
 * linked to a Document_Catalog_Entry by `catalogEntryId` (the additive link the
 * adapter resolves — Req 20.2/20.4).
 */
export interface DocumentRef {
  s3Key: string;
  fileName: string;
  contentType: string;
  /** Links the upload to its Document_Catalog_Entry, when configured. */
  catalogEntryId?: string;
}

/** The Person-sourced contact/identity fields surfaced read-only (Req 17.3). */
export interface ResolvedContact {
  legalFirstName: string;
  legalLastName: string;
  primaryEmail: string;
  mobilePhone?: string;
  dateOfBirth?: string;
  address?: PersonAddress;
}

/**
 * A read-model of the canonical entities a renderer/review/judge view needs to
 * resolve a Bound_Field's display value. Every field is optional so the adapter
 * can populate only the sources relevant to the bound fields on a given form;
 * `resolveBoundField` reads *only* the source matching the field's
 * Binding_Target (Req 10.6).
 */
export interface CanonicalSources {
  person?: ResolvedContact;
  participation?: { divisionKey?: string; approvedTitle?: string; competitionYear?: number };
  documents?: DocumentRef[];
  titlePreferences?: string[];
}

/**
 * The value a Bound_Field resolves to, discriminated by its Binding_Target. The
 * Contact/identity case always reports `locked: true` (Req 17.3). `present`
 * indicates whether the matching canonical source held a value.
 */
export type ResolvedBoundValue =
  | { target: 'DIVISION'; present: boolean; divisionKey: string | null }
  | {
      target: 'TITLE_PREFERENCES';
      present: boolean;
      ranked: string[];
      approvedTitle: string | null;
    }
  | {
      target: 'CONTACT_IDENTITY';
      present: boolean;
      locked: true;
      contact: ResolvedContact | null;
    }
  | { target: 'DOCUMENT_SLOT'; present: boolean; documents: DocumentRef[] }
  // Season selection resolves from Participation.competitionYear, the same
  // canonical source DIVISION reads (director-form-application-cutover, Req 4.2).
  | { target: 'SEASON'; present: boolean; competitionYear: number | null };

/**
 * Resolve a Bound_Field's display value from the canonical sources, reading
 * *only* the source that matches the field's Binding_Target (Req 9.1, 10.6):
 *
 *   - Contact/identity → `sources.person` (locked read-only, Req 17.3);
 *   - Division → `sources.participation.divisionKey`;
 *   - Title → `sources.titlePreferences` + `sources.participation.approvedTitle`;
 *   - Document slot → `sources.documents`.
 *
 * Pure and total over a Bound_Field. Throws a descriptive error when called with
 * a non-bound field or a bound field missing its `bindingTarget`, since that is
 * a programming error rather than a data condition the renderer can encounter.
 */
export function resolveBoundField(
  field: FormFieldDef,
  sources: CanonicalSources,
): ResolvedBoundValue {
  if (field.fieldCategory !== 'BOUND' || field.bindingTarget === undefined) {
    throw new Error(
      `resolveBoundField called on non-bound field "${field.id}"; a Binding_Target is required.`,
    );
  }

  switch (field.bindingTarget) {
    case 'CONTACT_IDENTITY': {
      const contact = sources.person ?? null;
      return { target: 'CONTACT_IDENTITY', present: contact !== null, locked: true, contact };
    }
    case 'DIVISION': {
      const divisionKey = sources.participation?.divisionKey ?? null;
      return { target: 'DIVISION', present: divisionKey !== null, divisionKey };
    }
    case 'TITLE_PREFERENCES': {
      const ranked = sources.titlePreferences ?? [];
      const approvedTitle = sources.participation?.approvedTitle ?? null;
      return {
        target: 'TITLE_PREFERENCES',
        present: ranked.length > 0 || approvedTitle !== null,
        ranked,
        approvedTitle,
      };
    }
    case 'DOCUMENT_SLOT': {
      const documents = sources.documents ?? [];
      return { target: 'DOCUMENT_SLOT', present: documents.length > 0, documents };
    }
    case 'SEASON': {
      const competitionYear = sources.participation?.competitionYear ?? null;
      return { target: 'SEASON', present: competitionYear !== null, competitionYear };
    }
  }
}

// ---------------------------------------------------------------------------
// Division selector (Req 18.1, 18.4)
// ---------------------------------------------------------------------------

/** A selectable option for a Division Bound_Field. */
export interface DivisionOption {
  key: string;
  label: string;
}

/**
 * The selectable options for a Division Bound_Field: the divisions that are
 * `active ∧ appliesToApplications` (i.e. application-selectable), in ascending
 * `displayOrder` so the dropdown is deterministic (Req 18.1). Divisions that are
 * inactive or not application-facing are omitted.
 */
export function divisionSelectorOptions(activeDivisions: readonly Division[]): DivisionOption[] {
  return activeDivisions
    .filter((division) => isApplicationSelectable(division))
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((division) => ({ key: division.key, label: division.label }));
}

/**
 * Validate an applicant's Division selection (Req 18.4). The selection is
 * accepted (returns `null`) iff a division with that key exists in the registry
 * and is `active ∧ appliesToApplications`; otherwise it is rejected with an
 * `INACTIVE_DIVISION` error. Pure and total.
 */
export function validateDivisionSelection(
  divisionKey: string,
  activeDivisions: readonly Division[],
): FieldValidationError | null {
  const selectable = activeDivisions.some(
    (division) => division.key === divisionKey && isApplicationSelectable(division),
  );
  if (selectable) {
    return null;
  }
  return {
    fieldId: divisionKey,
    code: 'INACTIVE_DIVISION',
    message: `Division "${divisionKey}" is not an active, application-selectable division for this competition.`,
  };
}

// ---------------------------------------------------------------------------
// Contact/identity lock (Req 17.3)
// ---------------------------------------------------------------------------

/**
 * Whether a Form_Field is a Contact/identity Bound_Field, which the renderer
 * presents as locked and read-only within the application (sourced from the
 * Person record captured at intake — Req 17.3). True iff the field is a
 * Bound_Field with Binding_Target `CONTACT_IDENTITY`.
 */
export function isContactIdentityLocked(field: FormFieldDef): boolean {
  return field.fieldCategory === 'BOUND' && field.bindingTarget === 'CONTACT_IDENTITY';
}
